import type { DashboardAnnualDaySlot } from './dashboardAnnualDaySlots';

const LAYOUT_KEY = 'cp.dashboard.weeklyCalendar.layout.v1';
const FIXED_EVENTS_ONLY_KEY = 'cp.dashboard.calendarFixedEventsOnly.v1';

export type DashboardWeeklyCalendarLayoutMode = 'fit' | 'expand';

export type DashboardCalendarPeriodChip<T extends CalendarTimetableSlotLike = CalendarTimetableSlotLike> = {
  ch: string;
  colorHex: string;
  source: T;
};

export type CalendarTimetableSlotLike = Pick<
  DashboardAnnualDaySlot,
  'timetableSlotKind' | 'timetableText' | 'timetableColorHex' | 'templateLabel'
> & {
  programKindLabel?: string;
  display?: Pick<DashboardAnnualDaySlot['display'], 'progressUnit'>;
  fixedContentEventName?: string | null;
};

export type CollectCalendarTimetableInitialsOptions = {
  /** 고정(행사) 프로그램 교시만 */
  fixedOnly?: boolean;
};

export function readDashboardWeeklyCalendarLayout(): DashboardWeeklyCalendarLayoutMode {
  if (typeof window === 'undefined') return 'expand';
  try {
    const raw = window.localStorage.getItem(LAYOUT_KEY);
    if (raw === 'fit' || raw === 'expand') return raw;
  } catch {
    /* ignore */
  }
  return 'expand';
}

export function writeDashboardWeeklyCalendarLayout(mode: DashboardWeeklyCalendarLayoutMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAYOUT_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function readDashboardCalendarFixedEventsOnly(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FIXED_EVENTS_ONLY_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDashboardCalendarFixedEventsOnly(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FIXED_EVENTS_ONLY_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** 편제명 첫 글자 (한글·영문 코드 포인트 1개) */
export function timetableProgramInitial(label: string): string {
  const t = label.trim();
  if (!t) return '';
  return Array.from(t)[0] ?? '';
}

export function timetableInitialTextColor(hex: string): string {
  const h = hex.replace('#', '').trim();
  if (h.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(h)) return '#111111';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const y = (r * 299 + g * 587 + b * 114) / 1000;
  return y < 150 ? '#ffffff' : '#111111';
}

function isCalendarTimetableSlot(slot: CalendarTimetableSlotLike): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return false;
  return slot.timetableText.trim().length > 0 || slot.timetableColorHex.trim().length > 0;
}

function normalizeFixedEventName(raw: string): string {
  return raw.trim().replace(/^\u25C6+/, '').replace(/-+$/, '').trim();
}

export function fixedEventDisplayName(slot: CalendarTimetableSlotLike): string {
  return normalizeFixedEventName(
    (slot.display?.progressUnit ?? '').trim() ||
      (slot.fixedContentEventName ?? '').trim() ||
      (slot.timetableText ?? '').trim() ||
      (slot.templateLabel ?? '').trim()
  );
}

/** 그날 고정(행사) 이름. 교시 순서, 같은 이름은 한 번만. */
export function collectFixedEventDisplayNames(slots: readonly CalendarTimetableSlotLike[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const slot of slots) {
    if (!isCalendarTimetableSlot(slot)) continue;
    if ((slot.programKindLabel ?? '').trim() !== '고정') continue;
    const name = fixedEventDisplayName(slot);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}

export function joinFixedEventDisplayLine(names: readonly string[]): string {
  return names.filter((n) => n.trim()).join(' · ');
}

/** 그날 교시 순서상 첫 고정(행사) 프로그램 색. 없으면 null. */
export function firstFixedProgramColorHex(
  slots: readonly CalendarTimetableSlotLike[]
): string | null {
  for (const slot of slots) {
    if (!isCalendarTimetableSlot(slot)) continue;
    if ((slot.programKindLabel ?? '').trim() !== '고정') continue;
    const hex = (slot.timetableColorHex ?? '').trim();
    if (hex) return hex;
  }
  return null;
}

/** 시간표에 편제가 있는 교시만, 편제명 첫 글자 + 색 + 원본 슬롯 */
export function collectCalendarTimetableInitials<T extends CalendarTimetableSlotLike>(
  slots: readonly T[],
  options?: CollectCalendarTimetableInitialsOptions
): DashboardCalendarPeriodChip<T>[] {
  const out: DashboardCalendarPeriodChip<T>[] = [];
  const fixedOnly = Boolean(options?.fixedOnly);
  for (const slot of slots) {
    if (!isCalendarTimetableSlot(slot)) continue;
    if (fixedOnly && (slot.programKindLabel ?? '').trim() !== '고정') continue;
    const label = (slot.templateLabel || slot.timetableText || '').trim();
    const ch = timetableProgramInitial(label);
    if (!ch) continue;
    out.push({ ch, colorHex: (slot.timetableColorHex ?? '').trim(), source: slot });
  }
  return out;
}
